'use client';

import styled, { css, TColorProp } from 'styled-components';

import { getThemeColor } from '~/common/utils/get-theme-color';
import { RADIUS } from '~/common/layout-propertis/radius';

import { TBadgeIconPosition, TBadgeSize, TBadgeVariant } from './types';

interface IContainerProps {
  $size: TBadgeSize;
  $variant: TBadgeVariant;
  $backgroundColor: TColorProp;
  $isDisabled: boolean;
  $isNumber: boolean;
  $shouldRenderIcon: boolean;
  $iconPosition: TBadgeIconPosition;
  $tintColor: TColorProp;
  $withLeftDot: boolean;
  $number: number;
  $borderColor: TColorProp;
  $cursor: string;
  $fixedWidth: string;
}

interface IDotWrapperProps {
  $size: TBadgeSize;
  $color: TColorProp;
}

const getHeight = {
  large: 24,
  medium: 20,
  small: 16,
} as Record<TBadgeSize, number>;

export const Container = styled.div<IContainerProps>`
  border-radius: ${RADIUS._999};
  background-color: ${({ theme, $backgroundColor }) =>
    getThemeColor(theme, $backgroundColor)};

  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $cursor }) => $cursor};

  img {
    width: ${({ $size }) => getHeight[$size]}px;
    height: ${({ $size }) => getHeight[$size]}px;
    margin-right: 4px;
  }

  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: ${({
    $isNumber,
    $iconPosition,
    $shouldRenderIcon,
    $withLeftDot,
    $number,
  }) =>
    $isNumber
      ? String($number).length > 1
        ? '4px'
        : '2px'
      : $withLeftDot
      ? '0px'
      : $shouldRenderIcon && $iconPosition === 'left'
      ? '4px'
      : '8px'};
  padding-right: ${({ $isNumber, $iconPosition, $shouldRenderIcon, $number }) =>
    $isNumber
      ? String($number).length > 1
        ? '4px'
        : '2px'
      : $shouldRenderIcon && $iconPosition === 'right'
      ? '4px'
      : '8px'};
  gap: 2px;

  min-height: ${({ $size }) => getHeight[$size]}px;
  min-width: ${({ $size, $isNumber }) =>
    $isNumber ? `${getHeight[$size]}px` : 'auto'};

  max-height: unset;
  text-align: center;

  ${({ $fixedWidth }) =>
    $fixedWidth &&
    css`
      width: ${$fixedWidth};
      max-width: ${$fixedWidth};
      min-width: ${$fixedWidth};
    `}

  ${({ $variant, theme, $tintColor, $borderColor }) =>
    $variant === 'outlined' &&
    css`
      outline: 1px solid ${getThemeColor(theme, $borderColor || $tintColor)};
    `}

  ${({ theme, $isDisabled }) =>
    $isDisabled &&
    css`
      outline: 1px solid ${theme.grey[700]};
      cursor: not-allowed;
    `}
`;

export const DotWrapper = styled.div<IDotWrapperProps>`
  width: ${({ $size }) => getHeight[$size]}px;
  height: ${({ $size }) => getHeight[$size]}px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: -2px;

  > span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: ${({ theme, $color }) => getThemeColor(theme, $color)};
  }
`;
